import { Currency } from "@xola/ui-kit";
import { isCancellationProtectionLineItem } from "common";
import { sumBy } from "lodash";
import React, { Fragment } from "react";
import { LineItemType, LineItemVisibility, Purchase, PurchaseItem } from "types";
import { useSeller } from "@/hooks/useAccount";

interface ReceiptSummaryTableProps {
    purchase: Purchase;
    item?: PurchaseItem;
    showWiderFormat: boolean;
}

export const ReceiptSummaryTable = ({ purchase, item, showWiderFormat }: ReceiptSummaryTableProps) => {
    return (
        <table
            style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: showWiderFormat ? 16 : 12,
            }}
        >
            {/* Wider format has a dedicated <thead> */}
            {showWiderFormat && (
                <thead>
                    <tr style={{ border: "1px solid black", fontWeight: "bold" }}>
                        <td style={{ padding: "7px", width: "50%" }}>Item</td>
                        <td style={{ padding: "7px", width: "30%" }}>Quantity</td>
                        <td style={{ padding: "7px", width: "20%" }}>Price</td>
                    </tr>
                </thead>
            )}

            <tbody>
                {/* Narrower format's header is inside the <tbody> */}
                {!showWiderFormat && (
                    <tr style={{ borderBottom: "1px solid black" }}>
                        <td>Item</td>
                        <td style={{ textAlign: "center" }}>Qty</td>
                        <td style={{ textAlign: "right" }}>Price</td>
                    </tr>
                )}

                {!showWiderFormat
                    ? purchase.items.map((item) => (
                          <Fragment key={item.id}>
                              <ReceiptSummaryTableBody item={item} showWiderFormat={showWiderFormat} />
                          </Fragment>
                      ))
                    : item && <ReceiptSummaryTableBody item={item} showWiderFormat={showWiderFormat} />}
            </tbody>
        </table>
    );
};

export const ReceiptSummaryTableBody = ({
    item,
    showWiderFormat,
}: {
    item: PurchaseItem;
    showWiderFormat: boolean;
}) => {
    const seller = useSeller();

    return (
        <Fragment key={item.id}>
            {/* The extra item name row only exists in the narrow format */}
            {!showWiderFormat && (
                <tr>
                    <td style={{ fontWeight: "bold" }} colSpan={3} className="no-translate">
                        {item.name}
                    </td>
                </tr>
            )}

            {getItemLineItems(item).map((lineItem) => (
                <tr key={lineItem.id} style={showWiderFormat ? { border: "1px solid black" } : {}}>
                    <td style={showWiderFormat ? { padding: "7px" } : {}} className="no-translate">
                        {lineItem.name}
                    </td>
                    <td
                        style={{
                            padding: showWiderFormat ? "7px" : "0",
                            textAlign: showWiderFormat ? "left" : "center",
                        }}
                        className="no-translate"
                    >
                        {lineItem.quantity}
                    </td>
                    <td
                        style={{
                            padding: showWiderFormat ? "7px" : "0",
                            textAlign: showWiderFormat ? "left" : "right",
                        }}
                    >
                        <Currency currency={seller.currency}>{lineItem.amount}</Currency>
                    </td>
                </tr>
            ))}

            {getTaxAndFeeLineItems(item, seller.preferences.checkout?.showTaxesAndFeesBreakdown).map(
                (lineItem, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index} style={showWiderFormat ? { border: "1px solid black" } : {}}>
                        <td colSpan={2} style={showWiderFormat ? { padding: "7px" } : {}} className="no-translate">
                            {lineItem.name}
                        </td>
                        <td
                            style={{
                                padding: showWiderFormat ? "7px" : "0",
                                textAlign: showWiderFormat ? "left" : "right",
                            }}
                        >
                            <Currency currency={seller.currency}>{lineItem.amount}</Currency>
                        </td>
                    </tr>
                ),
            )}

            {showWiderFormat ? (
                <tr>
                    <td colSpan={2} style={{ textAlign: "right", fontWeight: "bold", fontSize: 16, padding: "7px" }}>
                        Total
                    </td>
                    <td style={{ fontWeight: "bold", fontSize: 16, padding: "7px" }}>
                        <Currency currency={seller.currency}>{item.amount}</Currency>
                    </td>
                </tr>
            ) : (
                <tr style={{ borderTop: "1px solid black" }}>
                    <td style={{ fontWeight: "bold", paddingBottom: 16 }} colSpan={2}>
                        Sub-Total
                    </td>
                    <td style={{ textAlign: "right", paddingBottom: 16 }}>
                        <Currency currency={seller.currency}>{item.amount}</Currency>
                    </td>
                </tr>
            )}
        </Fragment>
    );
};

const getItemLineItems = (item: PurchaseItem) => {
    const filteredLineItems = item.lineItems.filter((lineItem) => {
        if (lineItem.type === LineItemType.DEMOGRAPHIC && (lineItem.quantity ?? 0) === 0 && lineItem.amount === 0) {
            return false;
        }

        return (
            lineItem.visibility !== LineItemVisibility.NONE &&
            lineItem.type !== LineItemType.TAX &&
            lineItem.type !== LineItemType.FEE &&
            !isCancellationProtectionLineItem(lineItem)
        );
    });

    return filteredLineItems.map((lineItem) => {
        if (lineItem.type === LineItemType.COUPON_DISCOUNT && lineItem.name) {
            // This regex will remove the text in parentheses at the end of the line item name
            // Implemented as part of bug fix X2-13318
            return { ...lineItem, name: lineItem.name.replace(/\s*\([^)]*\)$/, "") };
        }

        return lineItem;
    });
};

const getTaxAndFeeLineItems = (item: PurchaseItem, showTaxesAndFeesBreakdown = false) => {
    const taxAndFeeLineItems = item.lineItems.filter((lineItem) => {
        return (
            (lineItem.type === LineItemType.TAX || lineItem.type === LineItemType.FEE) &&
            lineItem.includeInAmount &&
            lineItem.amount > 0
        );
    });

    if (showTaxesAndFeesBreakdown) {
        return taxAndFeeLineItems;
    }

    const taxAndFeeSum = sumBy(taxAndFeeLineItems, (lineItem) => lineItem.amount);

    if (taxAndFeeSum > 0) {
        return [{ name: "Taxes & Fees", amount: taxAndFeeSum }];
    }

    return [];
};
